import React from 'react';
import { SEAT_LABELS } from '../utils/tableSeats';
import { formatBidLabel } from '../utils/bidLabel';
import ScoreBagsDisplay from './ScoreBagsDisplay';

const TEAM_LABELS = { ns: 'North / South', ew: 'East / West' };
const TEAM_SEATS = { ns: ['north', 'south'], ew: ['east', 'west'] };

function deltaClass(delta) {
  if (delta > 0) return 'spades-hand-delta--pos';
  if (delta < 0) return 'spades-hand-delta--neg';
  return '';
}

function formatDelta(delta) {
  if (delta == null) return '—';
  return delta > 0 ? `+${delta}` : String(delta);
}

function occupantLabel(occ) {
  if (!occ) return '—';
  return occ.username || occ.botLabel || '?';
}

function SpadesHandResults({
  handResults,
  seats,
  isHost,
  onNextHand,
  floating = false,
}) {
  if (!handResults) return null;

  const { handNumber, scoringMode, teams, players } = handResults;
  const isSingles = scoringMode === 'singles';

  const panelClass = [
    'spades-bid-panel',
    'spades-hand-results-panel',
    floating ? 'spades-bid-panel--float' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={panelClass} role="dialog" aria-label="Hand results">
      <p className="spades-bid-prompt">Hand {handNumber} complete</p>

      {isSingles ? (
        <ul className="spades-hand-results-list">
          {['north', 'south', 'east', 'west'].map((seat) => {
            const occ = seats?.[seat];
            if (!occ) return null;
            const ps = players?.[seat];
            if (!ps) return null;
            return (
              <li key={seat} className="spades-hand-results-row">
                <span className="spades-hand-results-name">
                  {occupantLabel(occ)}
                  <span className="spades-hand-results-seat">{SEAT_LABELS[seat]}</span>
                </span>
                <span className="spades-hand-results-bid">
                  {formatBidLabel(ps.bid)}
                  {' → '}
                  {ps.tricks ?? 0}
                  {ps.set ? ' (set)' : ''}
                </span>
                <span className={['spades-hand-results-delta', deltaClass(ps.delta)].join(' ')}>
                  {formatDelta(ps.delta)}
                </span>
                <ScoreBagsDisplay score={ps.score} bags={ps.bags} />
              </li>
            );
          })}
        </ul>
      ) : (
        <ul className="spades-hand-results-list">
          {['ns', 'ew'].map((teamKey) => {
            const team = teams?.[teamKey];
            if (!team) return null;
            const names = TEAM_SEATS[teamKey]
              .map((s) => occupantLabel(seats?.[s]))
              .filter((n) => n !== '—')
              .join(' · ');
            return (
              <li key={teamKey} className="spades-hand-results-row spades-hand-results-row--team">
                <span className="spades-hand-results-name">
                  {TEAM_LABELS[teamKey]}
                  <span className="spades-hand-results-seat">{names}</span>
                </span>
                <span className="spades-hand-results-bid">
                  Bid {team.teamBid ?? '—'}
                  {' · '}
                  {team.teamTricks ?? 0} tricks
                </span>
                <span className={['spades-hand-results-delta', deltaClass(team.delta)].join(' ')}>
                  {formatDelta(team.delta)}
                </span>
                <ScoreBagsDisplay score={team.score} bags={team.bags} />
              </li>
            );
          })}
        </ul>
      )}

      <div className="spades-hand-results-per-seat">
        {['north', 'south', 'east', 'west'].map((seat) => {
          const occ = seats?.[seat];
          const ps = players?.[seat];
          if (!occ || !ps || isSingles) return null;
          return (
            <div key={seat} className="spades-hand-results-seat-line">
              <span>{occupantLabel(occ)}</span>
              <span>
                {formatBidLabel(ps.bid)}
                {' → '}
                {ps.tricks ?? 0}
                {ps.set ? ' ✗' : ''}
              </span>
            </div>
          );
        })}
      </div>

      {isHost ? (
        <button
          type="button"
          className="spades-bid-btn spades-bid-btn--primary spades-host-btn--wide"
          onClick={onNextHand}
        >
          Deal next hand
        </button>
      ) : (
        <p className="spades-hand-results-wait">Waiting for host to deal next hand…</p>
      )}
    </div>
  );
}

export default SpadesHandResults;